import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaSpinner } from 'react-icons/fa';
import { FiDownload, FiPlay, FiX } from 'react-icons/fi';
import toast from 'react-hot-toast';
import axiosInstance from '../../libs/axios';
import { Pagination, PaginationType } from '../../utils/pagination.utils';

/**
 * Client View
 * ===========
 *
 * Look up one site of the caller's own projects by its complete Site ID —
 * nothing is listed until the user searches. The row shows Site ID, Project,
 * Activity and FW Start Date with two actions: download the engineer's field
 * data (photos and videos only, zipped in the browser, with a progress bar)
 * and manage the public slideshow, the same way the site view does.
 */

// Progress of the field-data ZIP: files fetched first, then the archive built.
interface DownloadProgress {
  phase: 'preparing' | 'downloading' | 'zipping';
  done: number;
  total: number;
  percent: number;
}

interface ClientViewRow {
  id: number;
  site_id: string | null;
  project_name: string | null;
  activity_name: string | null;
  fw_start_date: string | null;
}

interface SlideshowState {
  slug: string | null;
  expiry_at: string | null;
  is_expired: boolean;
  generated_by: { first_name: string; last_name: string; username: string } | null;
  last_regenerated_at: string | null;
  created_at: string | null;
}

interface NormalizedPagination {
  total_items: number;
  current_page: number;
  total_pages: number;
  items_per_page: number;
  has_next_page: boolean;
  has_previous_page: boolean;
}

const EMPTY_PAGINATION: NormalizedPagination = {
  total_items: 0,
  current_page: 1,
  total_pages: 1,
  items_per_page: 50,
  has_next_page: false,
  has_previous_page: false,
};

const formatDate = (value: string | null): string => {
  if (!value) return 'NA';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'NA';
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const formatSlideshowGeneratedBy = (
  user?: { first_name?: string; last_name?: string; username?: string } | null,
) => {
  if (!user) return 'Unknown user';
  const fullName = `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim();
  return fullName || user.username || 'Unknown user';
};

const ClientViewReport: React.FC = () => {
  const [activities, setActivities] = useState<{ id: number; activity_name: string }[]>([]);
  // What the user is typing vs. the Site ID actually searched. The table only
  // renders once a search has been submitted.
  const [siteIdInput, setSiteIdInput] = useState('');
  const [submittedSiteId, setSubmittedSiteId] = useState('');
  const [activityId, setActivityId] = useState('');

  const [rows, setRows] = useState<ClientViewRow[]>([]);
  const [pagination, setPagination] = useState<NormalizedPagination>(EMPTY_PAGINATION);
  const [loading, setLoading] = useState(false);

  const [downloadingSiteId, setDownloadingSiteId] = useState<number | null>(null);
  const [downloadProgress, setDownloadProgress] = useState<DownloadProgress | null>(null);

  // Slideshow modal — one site at a time
  const [slideshowSite, setSlideshowSite] = useState<ClientViewRow | null>(null);
  const [slideshowData, setSlideshowData] = useState<SlideshowState | null>(null);
  const [slideshowLoading, setSlideshowLoading] = useState(false);
  const [slideshowForm, setSlideshowForm] = useState({ expiry_days: 7 });

  useEffect(() => {
    axiosInstance
      .get('/reports/client-view/activities')
      .then((response) => setActivities(response.data?.data?.activities || []))
      .catch((error) => {
        console.error('[client-view] activities failed:', error);
        toast.error(error?.response?.data?.message || 'Failed to load activities');
      });
  }, []);

  const fetchRows = useCallback(
    async (targetPage: number, targetLimit: number) => {
      if (!submittedSiteId) return;
      try {
        setLoading(true);
        const params: Record<string, string | number> = {
          page: targetPage,
          limit: targetLimit,
          site_id: submittedSiteId,
        };
        if (activityId) params.activity_id = Number(activityId);

        const response = await axiosInstance.get('/reports/client-view', { params });
        if (!response.data?.success) return;

        const payload = response.data.data || {};
        const data: ClientViewRow[] = payload.data || [];
        const meta = payload.pagination || {};
        const total = Number(meta.total ?? data.length) || 0;
        const currentPage = Number(meta.current_page ?? targetPage) || targetPage;
        const perPage = Number(meta.per_page ?? targetLimit) || targetLimit;
        const totalPages = Number(meta.total_pages) || Math.max(1, Math.ceil(total / perPage));

        setRows(data);
        setPagination({
          total_items: total,
          current_page: currentPage,
          total_pages: totalPages,
          items_per_page: perPage,
          has_next_page: Boolean(meta.has_next_page ?? currentPage < totalPages),
          has_previous_page: Boolean(meta.has_prev_page ?? currentPage > 1),
        });
      } catch (error: any) {
        console.error(error);
        toast.error(error?.response?.data?.message || 'Failed to fetch sites');
      } finally {
        setLoading(false);
      }
    },
    [submittedSiteId, activityId],
  );

  useEffect(() => {
    fetchRows(1, pagination.items_per_page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchRows]);

  const handleSearch = (event?: React.FormEvent) => {
    event?.preventDefault();
    const value = siteIdInput.trim();
    if (!value) {
      toast.error('Enter the complete Site ID');
      return;
    }
    if (value === submittedSiteId) {
      // Same Site ID again — refetch explicitly, the effect won't re-run.
      fetchRows(1, pagination.items_per_page);
      return;
    }
    setRows([]);
    setPagination(EMPTY_PAGINATION);
    setSubmittedSiteId(value);
  };

  const handleClear = () => {
    setSiteIdInput('');
    setSubmittedSiteId('');
    setRows([]);
    setPagination(EMPTY_PAGINATION);
  };

  const paginationAdapter: PaginationType = useMemo(
    () => ({
      offset: Math.max((pagination.current_page - 1) * pagination.items_per_page, 0),
      limit: pagination.items_per_page,
      page: pagination.current_page,
      total_pages: pagination.total_pages,
      total_count: pagination.total_items,
      has_next_page: pagination.has_next_page,
      has_prev_page: pagination.has_previous_page,
      next_page: pagination.has_next_page ? pagination.current_page + 1 : null,
      prev_page: pagination.has_previous_page ? pagination.current_page - 1 : null,
    }),
    [pagination],
  );

  // ── Field data ZIP: engineer photos/videos only, zipped in the browser ────
  const handleDownloadFieldData = async (row: ClientViewRow) => {
    if (downloadingSiteId) return;
    setDownloadingSiteId(row.id);
    setDownloadProgress({ phase: 'preparing', done: 0, total: 0, percent: 0 });
    try {
      const response = await axiosInstance.get(
        `/reports/client-view/sites/${row.id}/field-data/presigned-urls`,
      );
      const files: Array<{ url: string; fileName: string }> = response.data?.data?.files || [];
      if (!files.length) {
        toast.error('No files to download');
        return;
      }

      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      const usedNames = new Map<string, number>();
      const entryNames = files.map((file) => {
        const baseName = (file.fileName || 'file').split(/[\\/]/).pop() || 'file';
        const key = baseName.toLowerCase();
        const seen = usedNames.get(key) || 0;
        usedNames.set(key, seen + 1);
        if (seen === 0) return baseName;
        const dotIdx = baseName.lastIndexOf('.');
        const ext = dotIdx >= 0 ? baseName.slice(dotIdx) : '';
        const stem = dotIdx >= 0 ? baseName.slice(0, dotIdx) : baseName;
        return `${stem}_${seen}${ext}`;
      });

      // Downloading is the long part, so it fills 0–90% of the bar and building
      // the archive the last 10%.
      let completed = 0;
      setDownloadProgress({ phase: 'downloading', done: 0, total: files.length, percent: 0 });
      await Promise.all(
        files.map(async (file, idx) => {
          try {
            const resp = await fetch(file.url);
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            zip.file(entryNames[idx], await resp.blob());
          } catch (err) {
            console.error(`Skipping file ${file.fileName}:`, err);
          } finally {
            completed += 1;
            setDownloadProgress({
              phase: 'downloading',
              done: completed,
              total: files.length,
              percent: Math.round((completed / files.length) * 90),
            });
          }
        }),
      );

      setDownloadProgress({ phase: 'zipping', done: files.length, total: files.length, percent: 90 });
      // Photos/videos are already compressed; STORE keeps zipping fast.
      const content = await zip.generateAsync({ type: 'blob', compression: 'STORE' }, (meta) => {
        setDownloadProgress({
          phase: 'zipping',
          done: files.length,
          total: files.length,
          percent: 90 + Math.round((meta.percent / 100) * 10),
        });
      });
      const now = new Date();
      const pad2 = (n: number) => String(n).padStart(2, '0');
      const stamp = `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}_${pad2(now.getHours())}-${pad2(now.getMinutes())}`;
      const blobUrl = window.URL.createObjectURL(content);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${row.site_id || row.id}_field-data_${stamp}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      toast.success(`Downloaded ${files.length} file(s)`);
    } catch (error: any) {
      console.error('Field data download failed:', error);
      toast.error(error?.response?.data?.message || 'Failed to download field data');
    } finally {
      setDownloadingSiteId(null);
      setDownloadProgress(null);
    }
  };

  // ── Slideshow (same flow as the site view) ────────────────────────────────
  const fetchSlideshow = async (siteId: number) => {
    try {
      const res = await axiosInstance.get(`/slideshow/sites/${siteId}`);
      setSlideshowData(res.data.success && res.data.data.slideshow ? res.data.data.slideshow : null);
    } catch {
      setSlideshowData(null);
    }
  };

  const openSlideshow = (row: ClientViewRow) => {
    setSlideshowSite(row);
    setSlideshowData(null);
    setSlideshowForm({ expiry_days: 7 });
    fetchSlideshow(row.id);
  };

  const closeSlideshow = () => {
    if (slideshowLoading) return;
    setSlideshowSite(null);
    setSlideshowData(null);
  };

  const handleCreateOrRegenerateSlideshow = async () => {
    if (!slideshowSite) return;
    setSlideshowLoading(true);
    try {
      await axiosInstance.post(`/slideshow/sites/${slideshowSite.id}`, { expiry_days: slideshowForm.expiry_days });
      await fetchSlideshow(slideshowSite.id);
    } catch {
      // toast shown by axios interceptor
    } finally {
      setSlideshowLoading(false);
    }
  };

  const handleExtendExpiry = async (days: number) => {
    if (!slideshowSite) return;
    setSlideshowLoading(true);
    try {
      await axiosInstance.patch(`/slideshow/sites/${slideshowSite.id}/extend`, { expiry_days: days });
      await fetchSlideshow(slideshowSite.id);
    } catch {
      // toast shown by axios interceptor
    } finally {
      setSlideshowLoading(false);
    }
  };

  const getSlideshowPublicUrl = () => {
    if (!slideshowData?.slug || !slideshowSite) return '';
    const fwDate = slideshowSite.fw_start_date ? slideshowSite.fw_start_date.slice(0, 10) : '';
    return `${window.location.origin}/slideshow/${slideshowData.slug}?site_id=${slideshowSite.site_id || ''}&fw_date=${fwDate}`;
  };

  const COLUMN_COUNT = 5;

  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded p-4">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Client View</h1>
            <p className="text-sm text-gray-600 mt-1">Enter the complete Site ID and click Search.</p>
          </div>
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
            <input
              type="text"
              value={siteIdInput}
              onChange={(event) => setSiteIdInput(event.target.value)}
              placeholder="Enter complete Site ID"
              className="w-full sm:w-64 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
            />
            <select
              value={activityId}
              onChange={(event) => setActivityId(event.target.value)}
              className="w-full sm:w-56 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
            >
              <option value="">All Activities</option>
              {activities.map((activity) => (
                <option key={activity.id} value={activity.id}>
                  {activity.activity_name}
                </option>
              ))}
            </select>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 rounded bg-blue-700 text-white text-sm font-medium hover:bg-blue-800 disabled:opacity-60"
            >
              Search
            </button>
            {submittedSiteId && (
              <button
                type="button"
                onClick={handleClear}
                className="px-4 py-2 rounded border border-gray-300 text-gray-700 text-sm hover:bg-gray-50"
              >
                Clear
              </button>
            )}
          </form>
        </div>
      </div>

      {downloadProgress && (
        <div className="bg-white border border-gray-200 rounded p-4">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="font-medium text-gray-800">
              {downloadProgress.phase === 'preparing'
                ? 'Preparing field data…'
                : downloadProgress.phase === 'downloading'
                  ? `Downloading files ${downloadProgress.done} / ${downloadProgress.total}`
                  : 'Creating ZIP…'}
            </span>
            <span className="text-gray-600 tabular-nums">{downloadProgress.percent}%</span>
          </div>
          <div
            className="w-full h-2.5 bg-gray-200 rounded-full overflow-hidden"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={downloadProgress.percent}
          >
            <div
              className="h-full bg-green-600 transition-all duration-200"
              style={{ width: `${downloadProgress.percent}%` }}
            />
          </div>
        </div>
      )}

      {!submittedSiteId ? (
        <div className="bg-white border border-dashed border-gray-300 rounded p-10 text-center text-sm text-gray-500">
          Search a complete Site ID to see its details.
        </div>
      ) : (

      <div className="bg-white shadow-sm rounded overflow-hidden border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['Site ID', 'Project', 'Activity', 'FW Start Date', 'Actions'].map((label) => (
                  <th key={label} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={COLUMN_COUNT} className="px-4 py-10 text-center text-sm text-gray-600">
                    <FaSpinner className="inline w-4 h-4 animate-spin mr-2" />
                    Loading...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={COLUMN_COUNT} className="px-4 py-10 text-center text-sm text-gray-600">
                    No site found with Site ID "{submittedSiteId}" in your projects.
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900 font-medium whitespace-nowrap">{row.site_id || 'NA'}</td>
                    <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{row.project_name || 'NA'}</td>
                    <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{row.activity_name || 'NA'}</td>
                    <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{formatDate(row.fw_start_date)}</td>
                    <td className="px-4 py-3 text-sm whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleDownloadFieldData(row)}
                          disabled={downloadingSiteId !== null}
                          className="flex items-center gap-2 px-3 py-1.5 text-green-700 bg-green-50 rounded hover:bg-green-100 transition-colors text-xs font-medium disabled:opacity-60"
                          title="Download field data photos and videos as ZIP"
                        >
                          {downloadingSiteId === row.id ? (
                            <FaSpinner className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <FiDownload size={14} />
                          )}
                          {downloadingSiteId === row.id
                            ? `Downloading${downloadProgress ? ` ${downloadProgress.percent}%` : '...'}`
                            : 'Download Field Data'}
                        </button>
                        <button
                          onClick={() => openSlideshow(row)}
                          className="flex items-center gap-2 px-3 py-1.5 text-purple-700 bg-purple-50 rounded hover:bg-purple-100 transition-colors text-xs font-medium"
                          title="Generate Slideshow"
                        >
                          <FiPlay size={14} />
                          Generate Slideshow
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <Pagination
          pagination={paginationAdapter}
          onPageChange={(nextPage) => fetchRows(nextPage, pagination.items_per_page)}
          onLimitChange={(nextLimit) => fetchRows(1, nextLimit)}
        />
      </div>
      )}

      {/* Slideshow Modal */}
      {slideshowSite && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={closeSlideshow} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
            <div className="bg-white rounded-lg shadow-2xl max-w-md w-full pointer-events-auto">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Site Slideshow</h3>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Generate a public URL for {slideshowSite.site_id || "this site"}'s media
                  </p>
                </div>
                <button onClick={closeSlideshow} className="p-2 rounded hover:bg-gray-100 text-gray-500" disabled={slideshowLoading}>
                  <FiX className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                {slideshowData && (
                  <div className={`p-3 rounded-lg border text-sm ${slideshowData.is_expired ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className={`font-medium ${slideshowData.is_expired ? 'text-red-700' : 'text-green-700'}`}>
                        {slideshowData.is_expired ? 'Expired Slideshow' : 'Active Slideshow'}
                      </span>
                      {!slideshowData.is_expired && (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(getSlideshowPublicUrl()).then(() => toast.success('URL copied!'));
                            }}
                            className="text-xs text-blue-600 hover:underline"
                          >
                            Copy URL
                          </button>
                          <button
                            onClick={() => window.open(getSlideshowPublicUrl(), '_blank', 'noopener,noreferrer')}
                            className="text-xs text-green-600 hover:underline"
                          >
                            Open URL
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="space-y-1 text-xs text-gray-600">
                      <div>
                        <span className="font-medium">Expires: </span>
                        {slideshowData.expiry_at ? new Date(slideshowData.expiry_at).toLocaleString() : '—'}
                      </div>
                      {slideshowData.generated_by && (
                        <div>
                          <span className="font-medium">Generated by: </span>
                          {formatSlideshowGeneratedBy(slideshowData.generated_by)}
                        </div>
                      )}
                      {(slideshowData.last_regenerated_at || slideshowData.created_at) && (
                        <div>
                          <span className="font-medium">Last regenerated: </span>
                          {new Date(slideshowData.last_regenerated_at || slideshowData.created_at || '').toLocaleString()}
                        </div>
                      )}
                    </div>
                    {!slideshowData.is_expired && (
                      <div className="mt-2 flex gap-2">
                        {[7, 14, 30].map((days) => (
                          <button
                            key={days}
                            onClick={() => handleExtendExpiry(days)}
                            disabled={slideshowLoading}
                            className="text-xs px-2 py-1 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                          >
                            +{days}d
                          </button>
                        ))}
                        <span className="text-xs text-gray-400 self-center">extend expiry</span>
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {slideshowData ? 'Regenerate with new expiry (days)' : 'Expiry (days from now)'}
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={30}
                    value={slideshowForm.expiry_days}
                    onChange={(e) => {
                      const val = parseInt(e.target.value);
                      if (!isNaN(val)) setSlideshowForm({ expiry_days: Math.min(30, Math.max(1, val)) });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                  />
                  <p className="text-xs text-gray-400 mt-1">1–30 days. Previous URL will be invalidated on regeneration.</p>
                </div>

                <div className="flex items-center justify-end gap-3 pt-2">
                  <button
                    onClick={closeSlideshow}
                    className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 text-sm"
                    disabled={slideshowLoading}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateOrRegenerateSlideshow}
                    disabled={slideshowLoading}
                    className="px-4 py-2 rounded text-white bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 font-medium text-sm"
                  >
                    {slideshowLoading ? 'Processing...' : slideshowData ? 'Regenerate URL' : 'Generate URL'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ClientViewReport;
